import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, TextField, Tabs, Tab, Divider,
  List, ListItemButton, CircularProgress, Stack, Autocomplete, Tooltip,
  IconButton, Snackbar, Alert, Link as MuiLink, FormControlLabel, Switch,
} from '@mui/material';
import {
  Sync as SyncIcon, CheckCircle as ConfirmIcon, Cancel as RejectIcon,
  OpenInNew as OpenInNewIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../services/api';

type ReviewRow = {
  link_id: string; note_id: string; title: string | null; note_created_at: string | null;
  web_url: string | null; match_method: string | null; confidence: number | null;
  patient_id: string | null; patient_name: string | null; patient_internal_id: string | null;
  candidates: Array<{ patient_id: string; name: string; internal_id?: string; score: number }> | null;
  status: string;
};

type NoteDetail = {
  id: string; title: string | null; web_url: string | null; note_created_at: string | null;
  summary_markdown: string | null; summary_text: string | null; transcript_text: string | null;
  transcript_segment_count: number | null;
};

type PatientOpt = { id: string; name: string; internal_id?: string | null; date_of_birth?: string | null; phone?: string | null };

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return s; }
};

const confColor = (c: number | null): 'success' | 'warning' | 'default' => {
  if (c == null) return 'default';
  if (c >= 0.999) return 'success';
  if (c >= 0.7) return 'warning';
  return 'default';
};

const STATUS_TABS = [
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'rejected', label: 'Rejected' },
];

const Granola: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [tab, setTab] = useState<string>('needs_review');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState<ReviewRow | null>(null);
  const [detail, setDetail] = useState<NoteDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [remap, setRemap] = useState<PatientOpt | null>(null);
  const [patientOpts, setPatientOpts] = useState<PatientOpt[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [patientQuery, setPatientQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const searchTimer = useRef<any>(null);

  const loadStatus = useCallback(async () => {
    try { const r = await api.get('/granola/status'); setStatus(r.data?.data || null); } catch (e) { /* noop */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/granola/review', { params: { status: tab, q: search || undefined, limit: 2000 } });
      setRows(r.data?.data || []);
      setTotal(r.data?.total || 0);
    } catch (e) { setSnack({ msg: 'Failed to load notes', sev: 'error' }); }
    finally { setLoadingList(false); }
  }, [tab, search]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadList(); }, [loadList]);

  // Auto-refresh list + status periodically (constant, consistent updates).
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { loadStatus(); loadList(); }, 20000);
    return () => clearInterval(id);
  }, [autoRefresh, loadStatus, loadList]);

  const openNote = useCallback(async (row: ReviewRow) => {
    setSelected(row);
    setDetail(null);
    setShowTranscript(false);
    // default the remap picker to the current recommendation
    setRemap(row.patient_id ? { id: row.patient_id, name: row.patient_name || 'Recommended', internal_id: row.patient_internal_id } : null);
    setLoadingDetail(true);
    try {
      const r = await api.get(`/granola/notes/${row.note_id}`);
      setDetail(r.data?.data || null);
    } catch (e) { setSnack({ msg: 'Failed to load note detail', sev: 'error' }); }
    finally { setLoadingDetail(false); }
  }, []);

  // Debounced patient search for the remap autocomplete.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!patientQuery || patientQuery.length < 2) { setPatientOpts([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchingPatients(true);
      try { const r = await api.get('/granola/patients/search', { params: { q: patientQuery } }); setPatientOpts(r.data?.data || []); }
      catch { setPatientOpts([]); }
      finally { setSearchingPatients(false); }
    }, 300);
  }, [patientQuery]);

  const doConfirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const body: any = {};
      if (remap && remap.id && remap.id !== selected.patient_id) body.patient_id = remap.id;
      else if (remap && remap.id) body.patient_id = remap.id; // confirm recommended explicitly
      await api.post(`/granola/links/${selected.link_id}/confirm`, body);
      setSnack({ msg: 'Match confirmed', sev: 'success' });
      await loadList(); await loadStatus();
      setSelected(null); setDetail(null);
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.error || 'Confirm failed', sev: 'error' });
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/granola/links/${selected.link_id}/reject`, {});
      setSnack({ msg: 'Match rejected', sev: 'info' });
      await loadList(); await loadStatus();
      setSelected(null); setDetail(null);
    } catch (e: any) { setSnack({ msg: 'Reject failed', sev: 'error' }); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/granola/links/${selected.link_id}/reset`, {});
      setSnack({ msg: 'Re-opened for review', sev: 'info' });
      await loadList(); await loadStatus();
      setSelected(null); setDetail(null);
    } catch (e: any) { setSnack({ msg: 'Reset failed', sev: 'error' }); }
    finally { setBusy(false); }
  };

  const doSync = async () => {
    setSyncing(true);
    try {
      await api.post('/granola/sync', {});
      setSnack({ msg: 'Sync complete', sev: 'success' });
      await loadStatus(); await loadList();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.error || 'Sync failed', sev: 'error' });
    } finally { setSyncing(false); }
  };

  const summaryText = useMemo(() => (detail?.summary_markdown || detail?.summary_text || '').trim(), [detail]);

  return (
    <Box sx={{ height: { xs: 'auto', md: 'calc(100vh - 112px)' }, overflow: { xs: 'visible', md: 'hidden' }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Granola Notes</Typography>
            <Typography variant="caption" color="text.secondary">
              Meeting/consultation notes pulled from Granola. Confirm a match to link a note to a patient — only confirmed notes are shown to the AI.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            {status && (
              <>
                <Chip size="small" label={`${status.notes} notes`} />
                <Chip size="small" color="warning" label={`${status.needs_review} to review`} />
                <Chip size="small" color="success" label={`${status.confirmed} confirmed`} />
              </>
            )}
            <FormControlLabel
              control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
              label={<Typography variant="caption">Auto</Typography>}
            />
            <Button size="small" variant="contained" startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />} onClick={doSync} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '380px 1fr' }, gap: 1.5, flex: 1, minHeight: 0 }}>
        {/* List */}
        <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ p: 1.5, pb: 0 }}>
            <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelected(null); setDetail(null); }} variant="fullWidth" sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 12 } }}>
              {STATUS_TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
            </Tabs>
            <TextField
              fullWidth size="small" placeholder="Search title or patient…" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ mt: 1 }}
              InputProps={{ endAdornment: (<IconButton size="small" onClick={() => loadList()}><RefreshIcon fontSize="small" /></IconButton>) }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {loadingList ? 'Loading…' : `${rows.length}${total > rows.length ? ' of ' + total : ''} notes`}
            </Typography>
          </Box>
          <Divider sx={{ mt: 1 }} />
          <List sx={{ overflow: 'auto', flex: 1, minHeight: 0, py: 0 }}>
            {rows.map((r) => (
              <ListItemButton key={r.link_id} selected={selected?.link_id === r.link_id} onClick={() => openNote(r)} sx={{ display: 'block', borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>{(r.title || '(untitled)').trim()}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{fmtDate(r.note_created_at)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {r.patient_name
                    ? <Chip size="small" color={confColor(r.confidence)} label={`${r.patient_name}${r.confidence != null ? ` · ${Math.round((r.confidence || 0) * 100)}%` : ''}`} />
                    : <Chip size="small" variant="outlined" label="No match — map manually" />}
                  {r.match_method && r.match_method !== 'none' && <Typography variant="caption" color="text.secondary">{r.match_method.replace('_', ' ')}</Typography>}
                </Box>
              </ListItemButton>
            ))}
            {!loadingList && !rows.length && <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No notes in this view.</Typography></Box>}
          </List>
        </Paper>

        {/* Detail */}
        <Paper sx={{ p: 2, overflow: 'auto', minHeight: 0 }}>
          {!selected ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">Select a note to review its match and content.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{(selected.title || '(untitled)').trim()}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(selected.note_created_at)}</Typography>
                </Box>
                {selected.web_url && (
                  <Tooltip title="Open in Granola">
                    <IconButton size="small" component={MuiLink} href={selected.web_url} target="_blank" rel="noopener"><OpenInNewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Match / confirm panel */}
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Patient match</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="body2">Recommendation:</Typography>
                  {selected.patient_name
                    ? <Chip size="small" color={confColor(selected.confidence)} label={`${selected.patient_name}${selected.confidence != null ? ` · ${Math.round((selected.confidence || 0) * 100)}%` : ''} (${(selected.match_method || '').replace('_', ' ')})`} />
                    : <Chip size="small" variant="outlined" label="No recommendation — pick a patient" />}
                </Box>
                {!!(selected.candidates && selected.candidates.length) && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Other candidates:</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {selected.candidates.map((c) => (
                        <Chip key={c.patient_id} size="small" variant={remap?.id === c.patient_id ? 'filled' : 'outlined'} color={remap?.id === c.patient_id ? 'primary' : 'default'}
                          label={`${c.name} · ${Math.round((c.score || 0) * 100)}%`}
                          onClick={() => setRemap({ id: c.patient_id, name: c.name, internal_id: c.internal_id })} />
                      ))}
                    </Box>
                  </Box>
                )}
                <Autocomplete
                  size="small"
                  options={patientOpts}
                  loading={searchingPatients}
                  value={remap}
                  filterOptions={(x) => x}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  getOptionLabel={(o) => (o ? `${o.name}${o.internal_id ? ' (' + o.internal_id + ')' : ''}` : '')}
                  onChange={(_, v) => setRemap(v)}
                  onInputChange={(_, v, reason) => { if (reason === 'input') setPatientQuery(v); }}
                  noOptionsText={patientQuery.trim().length < 2 ? 'Type at least 2 letters…' : (searchingPatients ? 'Searching…' : 'No matching patients')}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[o.internal_id ? `ID ${o.internal_id}` : null, o.date_of_birth ? `DOB ${o.date_of_birth}` : null, o.phone].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Search patient DB to remap" placeholder="Type a patient name or ID…"
                      InputProps={{ ...params.InputProps, endAdornment: (<>{searchingPatients ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                  )}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  {selected.status === 'needs_review' && (
                    <>
                      <Button variant="contained" color="success" startIcon={<ConfirmIcon />} disabled={busy || !remap} onClick={doConfirm}>Confirm match</Button>
                      <Button variant="outlined" color="error" startIcon={<RejectIcon />} disabled={busy} onClick={doReject}>Reject</Button>
                    </>
                  )}
                  {selected.status !== 'needs_review' && (
                    <>
                      <Chip label={selected.status} color={selected.status === 'confirmed' ? 'success' : 'default'} />
                      <Button variant="outlined" disabled={busy} onClick={doReset}>Re-open for review</Button>
                    </>
                  )}
                  {busy && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
                </Stack>
              </Paper>

              {/* Content */}
              {loadingDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
              ) : (
                <>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>AI Summary</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {summaryText || 'No summary available.'}
                      </Typography>
                    </Paper>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Transcript {detail?.transcript_segment_count ? `(${detail.transcript_segment_count} segments)` : ''}</Typography>
                      <Button size="small" onClick={() => setShowTranscript((s) => !s)} disabled={!detail?.transcript_text}>
                        {showTranscript ? 'Hide' : 'Show'} transcript
                      </Button>
                    </Box>
                    {showTranscript && (
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, maxHeight: 360, overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 12 }}>
                          {detail?.transcript_text || 'No transcript.'}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Granola;
